import { getVendorAndEmailFromCookies } from './utilfunctions.js';


const subscriptionMiscModule = (() => {
  let isInitialized = false;

  const init = () => {
    if (isInitialized) return;
    isInitialized = true;
  };

  const formatSubscriptionRenewalDate = (isoDate) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    const locale = getLanguage() || 'en-US';

    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };


  const fetchUserUsageData = async () => {
    try {
      const { folderId, email } = getVendorAndEmailFromCookies();
      const usageRes = await fetch(getApiUrl('get-user-usage', 'geoapi'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          folderId,
          customerEmail: email,
          sitename: getSiteNameForStripe(),
        }),
      });

      if (!usageRes.ok) return null;
      return await usageRes.json();
    } catch (err) {
      console.log('User usage fetch failed:', err);
      return null;
    }
  };

  const getStripeSubscriptionData = async () => {
    const { folderId, email } = getVendorAndEmailFromCookies();
    if (!folderId) return null;

    try {
      const res = await fetch(getApiUrl('get-stripe-subscription', 'geoapi'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folderId }),
      });

      if (!res.ok) return null;
      const data = await res.json();

      const usageData = await fetchUserUsageData(folderId, email);
      if (!usageData) return null;

      return {
        ...data,
        usedCount: usageData.usedCount,
        maxCountAllowed: usageData.maxCountAllowed,
      };
    } catch (err) {
      console.log('Subscription fetch failed:', err);
      return null;
    }
  };


  const renderStripeSubscriptionUI = (data) => {
    if (!data) return;

    const subBtn = document.getElementById('rightdrawer-subscriptions') || document.getElementById('rightdrawer-main-subscriptions');
    const existingStatusLabel = document.getElementById('rightdrawer-subscription-status');
    if (!subBtn || !existingStatusLabel) return;

    const statusRaw = data?.stripeStatus || '';
    const isActive = statusRaw === 'active';
    const isTrialing = statusRaw === 'trialing';
    const isPastDue = statusRaw === 'past_due';
    const isCancelled = statusRaw === 'canceled' || statusRaw === 'cancelled';

    subBtn.setAttribute('data-stripe-status', statusRaw || 'free');
    existingStatusLabel.style.display = 'block';

    const buttonText = subBtn.querySelector('span');
    if (buttonText) buttonText.textContent = 'Manage Subscription';

    let statusText = '';
    let usageText = '';
    const refresh = getI18n('refresh');

    const usageLabelText = `Used: ${data?.usedCount || 0} of ${data?.maxCountAllowed || refresh}`;

    if (isActive) {
      statusText = `Active – Renews on ${formatSubscriptionRenewalDate(data.stripeCurrentPeriodEnd)}`;
      existingStatusLabel.style.color = 'var(--text-success)';
    } else if (isTrialing) {
      statusText = `Trialing – Ends on ${formatSubscriptionRenewalDate(data.stripeCurrentPeriodEnd)}`;
      existingStatusLabel.style.color = 'var(--text-accent)';
    } else if (isPastDue) {
      statusText = `Past Due – Update payment info`;
      existingStatusLabel.style.color = 'var(--text-warning)';
    } else if (isCancelled) {
      statusText = `Cancelled – Ends on ${formatSubscriptionRenewalDate(data.stripeCurrentPeriodEnd)}`;
      existingStatusLabel.style.color = 'var(--text-danger)';
    } else {
      statusText = 'Upgrade';
      usageText = `Free Plan: ${data?.usedCount || 0} out of ${data?.maxCountAllowed || refresh} Used`;
      existingStatusLabel.style.display = 'none';
      if (buttonText) buttonText.textContent = 'Upgrade';
    }

    usageText ||= usageLabelText;
    existingStatusLabel.textContent = statusText;

    subBtn.style.display = 'flex';

    let usageLabel = document.getElementById('rightdrawer-subscription-usage');
    if (!usageLabel) {
      usageLabel = document.createElement('div');
      usageLabel.id = 'rightdrawer-subscription-usage';
      usageLabel.style.marginLeft = 'calc(24px + 0.625rem)';
      usageLabel.style.fontSize = '12px';
      usageLabel.style.color = 'var(--text-token-text-secondary)';
      subBtn.insertAdjacentElement('beforeend', usageLabel);
    }
    usageLabel.textContent = usageText;

    removeSkeletons();
  };

  // ✅ Combined usage:
  const fetchStripeSubscription = async () => {
    const data = await getStripeSubscriptionData();
    renderStripeSubscriptionUI(data);
  };



  const unload = () => {
    isInitialized = false;

    const subBtn = document.getElementById('rightdrawer-subscriptions') || document.getElementById('rightdrawer-main-subscriptions');
    if (subBtn) {
      subBtn.style.display = 'none';
      const buttonText = subBtn.querySelector('span');
      if (buttonText) buttonText.textContent = '';
    }

    const existingStatusLabel = document.getElementById('rightdrawer-subscription-status');
    if (existingStatusLabel) {
      existingStatusLabel.textContent = '';
      existingStatusLabel.style.color = '';

    }

    const usageLabel = document.getElementById('rightdrawer-subscription-usage');
    if (usageLabel) {
      usageLabel.remove();
    }

    removeSkeletons();
  };


  const getPlanUsageStatus = (usageData) => {
    if (
      !usageData ||
      typeof usageData.usedCount !== 'number' ||
      typeof usageData.maxCountAllowed !== 'number' ||
      typeof usageData.stripeStatus !== 'string'
    ) {
      return { hasUsedAllowance: false, stripeStatus: null };
    }

    const hasUsedAllowance = usageData.usedCount >= usageData.maxCountAllowed;
    const stripeStatus = usageData.stripeStatus;

    switch (stripeStatus) {
      case 'free':
        // TODO: logic for free users
        break;
      case 'Starter Plan':
        // TODO: logic for starter plan users
        break;
      case 'Pro Plan':
        // TODO: logic for pro plan users
        break;
      default:
        // TODO: logic for unknown plan
        break;
    }

    return { hasUsedAllowance, stripeStatus };
  };

  const checkUserPlanAndUsage = async () => {
    try {
      const data = await fetchUserUsageData();
      const { hasUsedAllowance, stripeStatus } = getPlanUsageStatus(data);
      if (hasUsedAllowance && stripeStatus==='free') {
        const locale = getLanguage();
        staticModalModule.init(`/${locale}/onlypricing`, 'staticModalContainer', event);
        console.log(`User has used up their quota for: ${stripeStatus}`);
        return false;
        // Redirect or show upgrade prompt
      } else {
        console.log(`User still has usage left under: ${stripeStatus}`);
        return true;
        // Allow further usage
      }
    } catch (err) {
      console.log('Error in checkUserPlanAndUsage:', err);
      return false;
      // Continue silently
    }
  };




  const removeSkeletons = () => {
    const skeletonClasses = [
      'skeleton-loader-rightdrawer',
      'skeleton-text-rightdrawer',
      'skeleton-inline-rightdrawer'
    ];

    document.querySelectorAll(skeletonClasses.map(cls => '.' + cls).join(',')).forEach(el => {
      skeletonClasses.forEach(cls => el.classList.remove(cls));
    });
  };

  const redirectToSubscriptionPortal = async () => {
    const { folderId } = getVendorAndEmailFromCookies();
    if (!folderId) return;

    try {
      const res = await fetch(getApiUrl('get-subscription-portal', 'geoapi'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folderId }),
      });

      if (!res.ok) return;

      const data = await res.json();
      if (data?.manageSubscriptionUrl) {
        window.location.href = data.manageSubscriptionUrl;
      }
    } catch (err) {
      console.log('Portal redirect failed:', err);
    }
  };

  return {
    init,
    fetchStripeSubscription,
    redirectToSubscriptionPortal,
    checkUserPlanAndUsage,
    fetchUserUsageData,
    unload,
  };
})();

export default subscriptionMiscModule;
